import React, { useEffect, useRef, useState } from "react";
import { Megaphone, Trash2, Play, Square, Save, Clock, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * SponsorDropsPanel — record / upload sponsor ads with a rotation
 * interval (minutes between airings). The studio surfaces a "Play next
 * sponsor" button which respects each drop's last_played_at + interval.
 *
 * Backend cap: 384KB / drop, 8 drops / creator. UI mirrors that.
 */
export default function SponsorDropsPanel({ onPlayDrop }) {
  const [drops, setDrops] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recordedUri, setRecordedUri] = useState(null);
  const [recordSec, setRecordSec] = useState(0);
  const [label, setLabel] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [interval, setIntervalMin] = useState(15);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickerRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dj/sponsor-drops");
      setDrops(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => setRecordedUri(reader.result);
        reader.readAsDataURL(blob);
      };
      mr.start();
      recRef.current = mr;
      startedAtRef.current = Date.now();
      setRecording(true);
      setRecordSec(0);
      tickerRef.current = setInterval(() => {
        setRecordSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
    } catch (e) {
      toast.error("Mic permission required to record sponsor drops");
    }
  };

  const stopRec = () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    setRecording(false);
  };

  // Upload an existing audio file as a sponsor drop. Server caps at
  // 384 KB / drop so we sanity-check before reading the file into a
  // data-URI.
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      toast.error("Pick an audio file (MP3, WAV, OGG, M4A, WEBM).");
      e.target.value = "";
      return;
    }
    const MAX = 384 * 1024;
    if (file.size > MAX) {
      toast.error(`File too large (${Math.round(file.size / 1024)} KB > ${MAX / 1024} KB). Trim or re-export at lower quality.`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setRecordedUri(reader.result);
      setRecordSec(0);
      if (!label.trim()) {
        const name = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").trim();
        setLabel(name.slice(0, 80));
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    if (!recordedUri || !label.trim()) {
      toast.info("Add a label and record a clip first.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/dj/sponsor-drops", {
        label: label.trim(),
        sponsor_name: sponsor.trim(),
        interval_min: interval,
        data_uri: recordedUri,
        duration_sec: recordSec,
      });
      setLabel(""); setSponsor(""); setRecordedUri(null); setRecordSec(0);
      await load();
      toast.success("Sponsor drop saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dj/sponsor-drops/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fire = async (d) => {
    try {
      onPlayDrop?.(d.data_uri);
      await api.post(`/dj/sponsor-drops/${d.id}/played`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const isReady = (d) => {
    if (!d.last_played_at) return true;
    const elapsed = (Date.now() - new Date(d.last_played_at).getTime()) / 60000;
    return elapsed >= (d.interval_min || 15);
  };

  return (
    <section data-testid="radio-host-sponsor-drops-panel" className="rounded-xl border border-white/10 bg-black/40 p-3 mb-4">
      <header className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Megaphone className="w-4 h-4 text-amber-300 shrink-0" />
          <h3 className="text-[11px] uppercase tracking-[0.25em] font-black text-white/85">Sponsor Drops</h3>
          <span className="text-[10px] text-white/45">{drops.length}/8</span>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <input
          data-testid="sponsor-drop-label-input"
          placeholder="Label (e.g. Nike spot)"
          value={label}
          onChange={(e) => setLabel(e.target.value.slice(0, 80))}
          className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white"
        />
        <input
          data-testid="sponsor-drop-sponsor-input"
          placeholder="Sponsor (optional)"
          value={sponsor}
          onChange={(e) => setSponsor(e.target.value.slice(0, 80))}
          className="bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white"
        />
      </div>
      <div className="flex items-center gap-2 mb-2 text-[11px] text-white/65">
        <Clock className="w-3.5 h-3.5" />
        <span>Every</span>
        <input
          data-testid="sponsor-drop-interval-input"
          type="number" min="2" max="240" step="1"
          value={interval}
          onChange={(e) => setIntervalMin(Math.max(2, Math.min(240, parseInt(e.target.value, 10) || 15)))}
          className="w-14 bg-black/40 border border-white/10 rounded px-1 py-0.5 text-white text-center"
        />
        <span>min</span>
        <div className="flex-1" />
        {!recording ? (
          <>
            <button
              data-testid="sponsor-drop-record-start"
              onClick={startRec}
              className="bg-rose-500/30 hover:bg-rose-500/50 text-rose-100 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1"
            ><Play className="w-3 h-3" /> Record</button>
            <button
              data-testid="sponsor-drop-upload"
              onClick={() => fileInputRef.current?.click()}
              className="bg-white/5 hover:bg-white/15 text-white/75 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1"
            ><Upload className="w-3 h-3" /> Upload</button>
            <input
              ref={fileInputRef}
              data-testid="sponsor-drop-file-input"
              type="file"
              accept="audio/*"
              onChange={handleUpload}
              className="hidden"
            />
          </>
        ) : (
          <button
            data-testid="sponsor-drop-record-stop"
            onClick={stopRec}
            className="bg-rose-500 hover:bg-rose-400 text-black text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1"
          ><Square className="w-3 h-3" /> {recordSec}s</button>
        )}
      </div>
      {recordedUri && (
        <div className="flex items-center gap-2 mb-2">
          <audio src={recordedUri} controls className="flex-1 h-8" />
          <button
            data-testid="sponsor-drop-save"
            onClick={save}
            disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black text-[10px] font-black uppercase px-2 py-1 rounded inline-flex items-center gap-1"
          ><Save className="w-3 h-3" /> Save</button>
        </div>
      )}

      {drops.length === 0 ? (
        <p className="text-[11px] text-white/40 italic">No sponsor drops yet — record one above.</p>
      ) : (
        <ul className="space-y-1.5 max-h-[260px] overflow-y-auto">
          {drops.map((d) => {
            const ready = isReady(d);
            return (
              <li
                key={d.id}
                data-testid={`sponsor-drop-${d.id}`}
                className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2 py-1.5"
              >
                <Megaphone className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-white font-bold truncate">{d.label}</div>
                  <div className="text-[10px] text-white/45 truncate">
                    {d.sponsor_name ? `${d.sponsor_name} · ` : ""}every {d.interval_min}min · played {d.play_count || 0}×
                  </div>
                </div>
                <button
                  data-testid={`sponsor-drop-${d.id}-fire`}
                  onClick={() => fire(d)}
                  disabled={!ready}
                  title={ready ? "Fire now" : "Cooling down"}
                  className={`p-1.5 rounded ${ready ? "bg-amber-500/30 hover:bg-amber-500/50 text-amber-100" : "bg-white/5 text-white/30 cursor-not-allowed"}`}
                ><Play className="w-3.5 h-3.5" /></button>
                <button
                  data-testid={`sponsor-drop-${d.id}-delete`}
                  onClick={() => remove(d.id)}
                  className="p-1.5 text-white/40 hover:text-rose-300"
                ><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
